import { useState, useEffect, useMemo } from "react";
import {
  savePurchaseOrder,
  getItemTypes,
  getItems,
  getUnits,
  getStores,
  getRequestedForOptions,
  getCurrencies
} from "../Api/PurchaseOrder";
import { getAllMerchants, getCompanyCodesByOrganisation } from "../Api/CustomerMaster";
import { Container, Form, Button, Row, Col, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Sample14 from "../Components/Logo";
import { useAuth } from "../Context/AuthContext";
import { fetchCompnayCodesById, fetchOfficeByOrgId } from "../Api/InvoiceApi";
import axios from "axios";
import { Base_URL } from "../url";

const VendorInvoiceWithoutPO = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const { user } = useAuth();

  // dropdown data
  const [merchants, setMerchants] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [offices, setOffices] = useState([]);
  const [stores, setStores] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [company, setCompany] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]); // tax codes now loaded on vendor change

  // per-item requestedForCode options map: { [index]: [{value,label}, ...] }
  const [requestedForCodeOptionsMap, setRequestedForCodeOptionsMap] = useState({});

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = userDetails?.userId;

  const staticRequestedForOptions = [
    { value: "Resource", label: "Resource" },
    { value: "Department", label: "Department" },
    { value: "Cost Center", label: "Cost Center" },
    { value: "Cost Center Group", label: "Cost Center Group" },
    { value: "Office", label: "Office" },
    { value: "Company", label: "Company" },
    { value: "Inventory", label: "Inventory" }
  ];

  // main order info — default type set to "Without PO"
  const [orderData, setOrderData] = useState({
    type: "Without PO",
    merchantCode: "",
    poDate: "",
    currency: "",
    orgId: "",
    companyCode: "",
    userId: user?.userId
  });

  // multiple items
  const [orderItems, setOrderItems] = useState([
    {
      purchaseItemType: "",
      item: "",
      quantity: "",
      itemDescription: "",
      unit: "",
      price: "",
      purchaseRequestedFor: "",
      purchaseRequestedForCode: "",
      officeOfSupply: "",
      storeOfSupply: "",
      taxCode: "", // we store description as the selected tax code
      cgst: 0,
      igst: 0,
      sgst: 0,
      cgstAmount: 0,
      igstAmount: 0,
      sgstAmount: 0,
      totalAmount: 0
    }
  ]);

  // 🔁 Initial dropdowns (no tax code here)
  useEffect(() => {
    const fetchDropdownData = async () => {
      setDropdownLoading(true);
      try {
        const [
          merchantsData,
          itemTypesData,
          itemsData,
          unitsData,
          officesData,
          storesData,
          currenciesData,
          companyData
        ] = await Promise.all([
          getAllMerchants(user?.organisationId),
          getItemTypes(),
          getItems(),
          getUnits(),
          fetchOfficeByOrgId(user?.organisationId),
          getStores(),
          getCurrencies(),
          fetchCompnayCodesById(user?.organisationId)
        ]);

        setMerchants(merchantsData || []);
        setItemTypes(itemTypesData || []);
        setItems(itemsData || []);
        setUnits(unitsData || []);
        setOffices(officesData || []);
        setStores(storesData || []);
        setCurrencies(currenciesData || []);
        setCompany(companyData || []);
      } catch (error) {
        console.error(error);
        toast.error("Error loading dropdown data");
      } finally {
        setDropdownLoading(false);
      }
    };
    fetchDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔁 Load tax codes when vendor is selected
  const fetchTaxCodesForVendor = async (vendorCode) => {
    // Clear tax data if vendor is cleared
    if (!vendorCode) {
      setTaxCodes([]);
      setOrderItems((prev) =>
        prev.map((row) => {
          const price = parseFloat(row.price || 0);
          const quantity = parseFloat(row.quantity || 0);
          const basicAmount = price * quantity;

          return {
            ...row,
            taxCode: "",
            cgst: 0,
            igst: 0,
            sgst: 0,
            cgstAmount: 0,
            igstAmount: 0,
            sgstAmount: 0,
            totalAmount: basicAmount
          };
        })
      );
      return;
    }

    try {
      setDropdownLoading(true);

      // You said API should not change, so we keep this endpoint as-is:
      const res = await axios.get(
        `${Base_URL}/VendorInvoice/getTaxCodeLogistics/${vendorCode}`
      );

      setTaxCodes(res?.data || []);

      // Reset selected tax codes on all rows when vendor changes
      setOrderItems((prev) =>
        prev.map((row) => {
          const price = parseFloat(row.price || 0);
          const quantity = parseFloat(row.quantity || 0);
          const basicAmount = price * quantity;

          return {
            ...row,
            taxCode: "",
            cgst: 0,
            igst: 0,
            sgst: 0,
            cgstAmount: 0,
            igstAmount: 0,
            sgstAmount: 0,
            totalAmount: basicAmount
          };
        })
      );
    } catch (error) {
      console.error("Error loading tax codes for vendor:", error);
      toast.error("Error loading tax codes for selected vendor");
      setTaxCodes([]);
    } finally {
      setDropdownLoading(false);
    }
  };

  // Fetch GST data from normal HSN logic for a specific item
  const fetchGstDataForItem = async (itemCode) => {
    if (!itemCode) {
      return { CGST: 0, IGST: 0, SGST: 0 };
    }

    try {
      console.log("Fetching GST data for item:", itemCode);
      const response = await axios.get(
        `${Base_URL}/PurchaseOrder/GetGst?itemCode=${encodeURIComponent(itemCode)}`
      );
      console.log("GST API Response:", response.data);

      if (response.data) {
        const data = response.data;

        let gstRates = {
          CGST: 0,
          IGST: 0,
          SGST: 0
        };

        if (data.cgsT_Percent !== undefined) {
          gstRates = {
            CGST: parseFloat(data.cgsT_Percent) || 0,
            IGST: parseFloat(data.igsT_Percent) || 0,
            SGST: parseFloat(data.sgsT_Percent) || 0
          };
        } else if (Array.isArray(data) && data.length > 0) {
          const firstItem = data[0];
          gstRates = {
            CGST: parseFloat(firstItem.cgsT_Percent) || 0,
            IGST: parseFloat(firstItem.igsT_Percent) || 0,
            SGST: parseFloat(firstItem.sgsT_Percent) || 0
          };
        }

        console.log("Final GST Rates for item", itemCode, ":", gstRates);
        return gstRates;
      }

      return { CGST: 0, IGST: 0, SGST: 0 };
    } catch (error) {
      console.error("Error fetching GST data for item", itemCode, ":", error);
      console.error("Error details:", error.response?.data);
      return { CGST: 0, IGST: 0, SGST: 0 };
    }
  };

  const fetchStoresForCompany = async (companyCode) => {
    if (!companyCode) {
      setStores([]);
      return;
    }
    setDropdownLoading(true);
    try {
      const res = await axios.get(
        `${Base_URL}/StoreMaster/id?id=${encodeURIComponent(companyCode)}`
      );

      if (res.status !== 200) {
        throw new Error(`Failed to fetch stores: ${res.status}`);
      }
      let payload = res.data;
      let list = [];

      if (Array.isArray(payload)) {
        list = payload;
      } else if (payload && typeof payload === "object") {
        if (Array.isArray(payload.data)) list = payload.data;
        else if (Array.isArray(payload.stores)) list = payload.stores;
        else if (Array.isArray(payload.result)) list = payload.result;
        else {
          const firstArray = Object.values(payload).find(Array.isArray);
          if (Array.isArray(firstArray)) list = firstArray;
        }
      }

      setStores(list || []);
    } catch (err) {
      console.error("Error fetching stores:", err);
      toast.error("Error loading stores for selected company");
      setStores([]);
    } finally {
      setDropdownLoading(false);
    }
  };

  const calculateGstAmounts = (price, quantity, cgstRate, igstRate, sgstRate) => {
    if (!price || !quantity) {
      return {
        cgstAmount: 0,
        igstAmount: 0,
        sgstAmount: 0,
        totalAmount: 0
      };
    }

    const amount = parseFloat(price) * parseFloat(quantity);
    const cgstAmount = (amount * (cgstRate || 0)) / 100;
    const igstAmount = (amount * (igstRate || 0)) / 100;
    const sgstAmount = (amount * (sgstRate || 0)) / 100;
    const totalAmount = amount + cgstAmount + igstAmount + sgstAmount;

    return {
      cgstAmount: parseFloat(cgstAmount.toFixed(2)),
      igstAmount: parseFloat(igstAmount.toFixed(2)),
      sgstAmount: parseFloat(sgstAmount.toFixed(2)),
      totalAmount: parseFloat(totalAmount.toFixed(2))
    };
  };

  const orderSummary = useMemo(() => {
    const toNum = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };

    return (orderItems || []).reduce(
      (acc, it) => {
        const qty = toNum(it.quantity);
        const price = toNum(it.price);
        const lineBasic = qty * price;

        acc.basic += lineBasic;
        acc.cgst += toNum(it.cgstAmount);
        acc.sgst += toNum(it.sgstAmount);
        acc.igst += toNum(it.igstAmount);
        return acc;
      },
      { basic: 0, cgst: 0, sgst: 0, igst: 0 }
    );
  }, [orderItems]);

  const taxTotal = orderSummary.cgst + orderSummary.sgst + orderSummary.igst;
  const grandTotal = orderSummary.basic + taxTotal;

  // 🔁 vendor selection now triggers tax code loading
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "companyCode") {
      setOrderData((prev) => ({ ...prev, [name]: value }));
      fetchStoresForCompany(value)
        .then(() => {
          setOrderItems((prevItems) => {
            const copy = prevItems.map((it) => ({ ...it, storeOfSupply: "" }));
            return copy;
          });
        })
        .catch((err) => {});
      return;
    }

    if (name === "merchantCode") {
      setOrderData((prev) => ({ ...prev, [name]: value }));
      fetchTaxCodesForVendor(value);
      return;
    }

    setOrderData((prev) => ({ ...prev, [name]: value }));
  };

  const loadRequestedForCodesForIndex = async (requestedForValue, index) => {
    if (!requestedForValue) {
      setRequestedForCodeOptionsMap((prev) => {
        const copy = { ...prev };
        delete copy[index];
        return copy;
      });
      setOrderItems((prev) => {
        const copy = [...prev];
        if (copy[index]) copy[index].purchaseRequestedForCode = "";
        return copy;
      });
      return;
    }

    try {
      const options = await getRequestedForOptions(requestedForValue, user?.organisationId);
      setRequestedForCodeOptionsMap((prev) => ({ ...prev, [index]: options || [] }));
      setOrderItems((prev) => {
        const copy = [...prev];
        if (copy[index]) copy[index].purchaseRequestedForCode = "";
        return copy;
      });
    } catch (err) {
      console.error(err);
      toast.error(`Error loading ${requestedForValue} options for item ${index + 1}`);
      setRequestedForCodeOptionsMap((prev) => ({ ...prev, [index]: [] }));
      setOrderItems((prev) => {
        const copy = [...prev];
        if (copy[index]) copy[index].purchaseRequestedForCode = "";
        return copy;
      });
    }
  };

  const getFilteredItemsForRow = (rowIndex) => {
    const row = orderItems[rowIndex] || {};
    const selectedType = row.purchaseItemType ?? "";

    if (!selectedType) return items || [];

    const prefix = String(selectedType).slice(0, 4);

    return (items || []).filter((it) => {
      const code = String(it.value ?? it.code ?? it.itemCode ?? it.ItemCode ?? "");
      return code.startsWith(prefix);
    });
  };

  // ✅ FIXED: use description instead of code
  const applyTaxCodeToRow = (updatedItems, index, taxCodeValue, showToast = true) => {
    const row = updatedItems[index];

    // We store taxCode as "description", so match by description
    const selectedTax = taxCodes.find(
      (t) => String(t.description) === String(taxCodeValue)
    );

    if (!selectedTax) {
      return;
    }

    const cgstRate = parseFloat(selectedTax.cgst) || 0;
    const sgstRate = parseFloat(selectedTax.sgst) || 0;
    const igstRate = parseFloat(selectedTax.igst) || 0;

    row.cgst = cgstRate;
    row.sgst = sgstRate;
    row.igst = igstRate;

    const price = row.price || 0;
    const quantity = row.quantity || 0;
    const gstAmounts = calculateGstAmounts(price, quantity, cgstRate, igstRate, sgstRate);

    row.cgstAmount = gstAmounts.cgstAmount;
    row.sgstAmount = gstAmounts.sgstAmount;
    row.igstAmount = gstAmounts.igstAmount;
    row.totalAmount = gstAmounts.totalAmount;

    if (showToast) {
      toast.success(
        `Tax Code "${selectedTax.description}" applied: CGST ${cgstRate}%, SGST ${sgstRate}%, IGST ${igstRate}%`
      );
    }
  };

  const handleItemChange = async (index, e) => {
    const { name, value } = e.target;
    const updatedItems = [...orderItems];

    // set the field
    updatedItems[index][name] = value;

    if (name === "purchaseItemType") {
      // Clear item and GST data when type changes
      updatedItems[index].item = "";
      updatedItems[index].purchaseRequestedForCode = "";
      updatedItems[index].taxCode = "";
      updatedItems[index].cgst = 0;
      updatedItems[index].igst = 0;
      updatedItems[index].sgst = 0;
      updatedItems[index].cgstAmount = 0;
      updatedItems[index].igstAmount = 0;
      updatedItems[index].sgstAmount = 0;
      updatedItems[index].totalAmount = 0;
    }

    // When tax code is changed
    if (name === "taxCode") {
      const row = updatedItems[index];

      if (value) {
        // Apply GST from tax code
        applyTaxCodeToRow(updatedItems, index, value);
      } else {
        // Tax code cleared → fall back to normal HSN GST (if item is present)
        if (row.item) {
          try {
            setDropdownLoading(true);
            const gstRates = await fetchGstDataForItem(row.item);
            row.cgst = gstRates.CGST;
            row.igst = gstRates.IGST;
            row.sgst = gstRates.SGST;

            const price = row.price || 0;
            const quantity = row.quantity || 0;
            const gstAmounts = calculateGstAmounts(
              price,
              quantity,
              gstRates.CGST,
              gstRates.IGST,
              gstRates.SGST
            );

            row.cgstAmount = gstAmounts.cgstAmount;
            row.igstAmount = gstAmounts.igstAmount;
            row.sgstAmount = gstAmounts.sgstAmount;
            row.totalAmount = gstAmounts.totalAmount;

            toast.info("GST rates loaded from HSN (item GST)");
          } catch (err) {
            console.error("Error loading GST from HSN after clearing tax code:", err);
            row.cgst = 0;
            row.igst = 0;
            row.sgst = 0;
            row.cgstAmount = 0;
            row.igstAmount = 0;
            row.sgstAmount = 0;
            const price = row.price || 0;
            const quantity = row.quantity || 0;
            row.totalAmount = parseFloat(price || 0) * parseFloat(quantity || 0);
          } finally {
            setDropdownLoading(false);
          }
        } else {
          // No item, simply clear GST
          row.cgst = 0;
          row.igst = 0;
          row.sgst = 0;
          row.cgstAmount = 0;
          row.igstAmount = 0;
          row.sgstAmount = 0;
          const price = row.price || 0;
          const quantity = row.quantity || 0;
          row.totalAmount = parseFloat(price || 0) * parseFloat(quantity || 0);
        }
      }

      setOrderItems(updatedItems);
      return;
    }

    // When item is selected, fetch GST data for that specific item
    if (name === "item" && value) {
      setDropdownLoading(true);
      try {
        console.log("Item changed:", value);

        // If a tax code is already selected, use that instead of HSN GST
        if (updatedItems[index].taxCode) {
          applyTaxCodeToRow(
            updatedItems,
            index,
            updatedItems[index].taxCode,
            false
          );
          toast.success("GST rates loaded from selected Tax Code");
        } else {
          console.log("Fetching GST for new item (HSN):", value);
          const gstRates = await fetchGstDataForItem(value);

          console.log("Updating GST rates for item:", value, "Rates:", gstRates);

          updatedItems[index].cgst = gstRates.CGST;
          updatedItems[index].igst = gstRates.IGST;
          updatedItems[index].sgst = gstRates.SGST;

          const price = updatedItems[index].price || 0;
          const quantity = updatedItems[index].quantity || 0;
          const gstAmounts = calculateGstAmounts(
            price,
            quantity,
            gstRates.CGST,
            gstRates.IGST,
            gstRates.SGST
          );

          updatedItems[index].cgstAmount = gstAmounts.cgstAmount;
          updatedItems[index].igstAmount = gstAmounts.igstAmount;
          updatedItems[index].sgstAmount = gstAmounts.sgstAmount;
          updatedItems[index].totalAmount = gstAmounts.totalAmount;

          console.log("Updated item with new GST data:", updatedItems[index]);
          toast.success(
            `GST rates loaded (HSN): CGST ${gstRates.CGST}%, SGST ${gstRates.SGST}%, IGST ${gstRates.IGST}%`
          );
        }
      } catch (error) {
        console.error("Error loading GST rates for item:", error);
        updatedItems[index].cgst = 0;
        updatedItems[index].igst = 0;
        updatedItems[index].sgst = 0;
        updatedItems[index].cgstAmount = 0;
        updatedItems[index].igstAmount = 0;
        updatedItems[index].sgstAmount = 0;
        const price = updatedItems[index].price || 0;
        const quantity = updatedItems[index].quantity || 0;
        updatedItems[index].totalAmount =
          parseFloat(price || 0) * parseFloat(quantity || 0);
        toast.warning("Using default GST rates (0%)");
      } finally {
        setDropdownLoading(false);
      }
    }

    // Calculate GST amounts when price or quantity changes
    if (
      (name === "price" || name === "quantity") &&
      updatedItems[index].item
    ) {
      const price = name === "price" ? value : updatedItems[index].price;
      const quantity =
        name === "quantity" ? value : updatedItems[index].quantity;
      const cgstRate = updatedItems[index].cgst || 0;
      const igstRate = updatedItems[index].igst || 0;
      const sgstRate = updatedItems[index].sgst || 0;

      console.log("Recalculating for price/quantity change:", {
        price,
        quantity,
        cgstRate,
        igstRate,
        sgstRate
      });

      const gstAmounts = calculateGstAmounts(
        price,
        quantity,
        cgstRate,
        igstRate,
        sgstRate
      );
      updatedItems[index].cgstAmount = gstAmounts.cgstAmount;
      updatedItems[index].igstAmount = gstAmounts.igstAmount;
      updatedItems[index].sgstAmount = gstAmounts.sgstAmount;
      updatedItems[index].totalAmount = gstAmounts.totalAmount;
    }

    setOrderItems(updatedItems);

    if (name === "purchaseRequestedFor") {
      loadRequestedForCodesForIndex(value, index);
    }
  };

  const addItemRow = () => {
    setOrderItems((prev) => [
      ...prev,
      {
        purchaseItemType: "",
        item: "",
        quantity: "",
        itemDescription: "",
        unit: "",
        price: "",
        purchaseRequestedFor: "",
        purchaseRequestedForCode: "",
        officeOfSupply: "",
        storeOfSupply: "",
        taxCode: "",
        cgst: 0,
        igst: 0,
        sgst: 0,
        cgstAmount: 0,
        igstAmount: 0,
        sgstAmount: 0,
        totalAmount: 0
      }
    ]);
  };

  const removeItemRow = (index) => {
    setOrderItems((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      return updated;
    });

    setRequestedForCodeOptionsMap((prev) => {
      const copy = { ...prev };
      delete copy[index];
      return copy;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...orderData, items: orderItems };
      console.log("Submitting Vendor Invoice (Without PO):", payload);
      const res = await axios.post(
        `${Base_URL}/VendorInvoice/PostInvoiceWithoutPO`,
        payload
      );
      if (res.status === 200)
        toast.success(
          ` 🦄 ${res.data.vendorInvoiceeNumber}  Purchase Order Added Successfully`
        );
    } catch (error) {
      console.error(error);
      toast.error("🦄 Some Error Happened");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="main-content expand">
        <Container fluid className="fields-container">
          <h2 className="fields-title">Create Vendor Invoice</h2>

          <Form onSubmit={handleSubmit} className="fields-form">
            {/* BASIC DETAILS */}
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Currency</Form.Label>
                  <Form.Control
                    as="select"
                    name="currency"
                    value={orderData.currency}
                    onChange={handleChange}
                    required
                    disabled={dropdownLoading}
                  >
                    <option value="">Select Currency</option>
                    {currencies.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Vendor</Form.Label>
                  <Form.Control
                    as="select"
                    name="merchantCode"
                    value={orderData.merchantCode}
                    onChange={handleChange}
                    required
                    disabled={dropdownLoading}
                  >
                    <option value="">Vendor</option>
                    {merchants.map((m) => (
                      <option key={m.vendorCode} value={m.vendorCode}>
                        {m.vendorCode}-{m.name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Vendor Invoice Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="poDate"
                    value={orderData.poDate}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Company Code</Form.Label>
                  <Form.Control
                    as="select"
                    name="companyCode"
                    value={orderData.companyCode}
                    onChange={handleChange}
                    required
                    disabled={dropdownLoading}
                  >
                    <option value="">Company Code</option>
                    {company.map((m) => (
                      <option key={m.companyCode} value={m.companyCode}>
                        {m.companyCode}-{m.name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>

            {/* ORDER SUMMARY */}
            <h4 className="mt-4 mb-2">Order Summary</h4>
            <Table
              bordered
              hover
              responsive
              className="mb-3 text-center align-middle"
            >
              <thead>
                <tr>
                  <th>Basic Value</th>
                  <th>CGST (Total)</th>
                  <th>SGST (Total)</th>
                  <th>IGST (Total)</th>
                  <th>Tax Total</th>
                  <th>Grand Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{orderSummary.basic.toFixed(2)}</td>
                  <td>{orderSummary.cgst.toFixed(2)}</td>
                  <td>{orderSummary.sgst.toFixed(2)}</td>
                  <td>{orderSummary.igst.toFixed(2)}</td>
                  <td>{taxTotal.toFixed(2)}</td>
                  <td className="fw-bold">{grandTotal.toFixed(2)}</td>
                </tr>
              </tbody>
            </Table>

            {/* MULTIPLE ITEMS SECTION */}
            <h4 className="mt-3 mb-2">Items</h4>

            {orderItems.map((item, index) => (
              <div key={index} className="border rounded p-3 mb-3">
                <Row>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Item Type</Form.Label>
                      <Form.Control
                        as="select"
                        name="purchaseItemType"
                        value={item.purchaseItemType}
                        onChange={(e) => handleItemChange(index, e)}
                        required
                      >
                        <option value="">Select Type</option>
                        {itemTypes.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Item</Form.Label>
                      <Form.Control
                        as="select"
                        name="item"
                        value={item.item}
                        onChange={(e) => handleItemChange(index, e)}
                        required
                        disabled={!item.purchaseItemType || dropdownLoading}
                      >
                        <option value="">Select Item</option>
                        {getFilteredItemsForRow(index).length === 0 &&
                          item.purchaseItemType && (
                            <option value="" disabled>
                              No items match the selected type
                            </option>
                          )}

                        {getFilteredItemsForRow(index).map((opt) => (
                          <option
                            key={
                              opt.value ??
                              opt.code ??
                              opt.itemCode ??
                              JSON.stringify(opt)
                            }
                            value={opt.value ?? opt.code ?? opt.itemCode ?? ""}
                          >
                            {opt.label ??
                              opt.name ??
                              opt.description ??
                              (opt.value ?? opt.code ?? "")}
                          </option>
                        ))}
                      </Form.Control>
                      {dropdownLoading && item.item && (
                        <small className="text-muted">
                          Loading GST rates...
                        </small>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Qty</Form.Label>
                      <Form.Control
                        type="number"
                        name="quantity"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, e)}
                        required
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Unit</Form.Label>
                      <Form.Control
                        as="select"
                        name="unit"
                        value={item.unit}
                        onChange={(e) => handleItemChange(index, e)}
                        required
                      >
                        <option value="">Select Unit</option>
                        {units.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Price</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="price"
                        value={item.price}
                        onChange={(e) => handleItemChange(index, e)}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* TAX CODE + GST SECTION */}
                <Row className="mt-2">
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tax Code (Logistics)</Form.Label>
                      <Form.Control
                        as="select"
                        name="taxCode"
                        value={item.taxCode || ""}
                        onChange={(e) => handleItemChange(index, e)}
                        disabled={dropdownLoading || taxCodes.length === 0}
                      >
                        <option value="">Select Tax Code</option>
                        {taxCodes.map((tc, idx) => (
                          <option
                            key={`${tc.description}-${idx}`}
                            value={tc.description}
                          >
                            {tc.description}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mt-2">
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>CGST (%)</Form.Label>
                      <Form.Control
                        type="text"
                        value={item.cgst}
                        readOnly
                        className="bg-light"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>CGST Amount</Form.Label>
                      <Form.Control
                        type="text"
                        value={item.cgstAmount.toFixed(2)}
                        readOnly
                        className="bg-light"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>SGST (%)</Form.Label>
                      <Form.Control
                        type="text"
                        value={item.sgst}
                        readOnly
                        className="bg-light"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>SGST Amount</Form.Label>
                      <Form.Control
                        type="text"
                        value={item.sgstAmount.toFixed(2)}
                        readOnly
                        className="bg-light"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>IGST (%)</Form.Label>
                      <Form.Control
                        type="text"
                        value={item.igst}
                        readOnly
                        className="bg-light"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>IGST Amount</Form.Label>
                      <Form.Control
                        type="text"
                        value={item.igstAmount.toFixed(2)}
                        readOnly
                        className="bg-light"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mt-2">
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Total Amount</Form.Label>
                      <Form.Control
                        type="text"
                        value={item.totalAmount.toFixed(2)}
                        readOnly
                        className="bg-light fw-bold"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mt-2">
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Requested For</Form.Label>
                      <Form.Control
                        as="select"
                        name="purchaseRequestedFor"
                        value={item.purchaseRequestedFor}
                        onChange={(e) => handleItemChange(index, e)}
                      >
                        <option value="">Select Requested For</option>
                        {staticRequestedForOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Requested For Code</Form.Label>
                      <Form.Control
                        as="select"
                        name="purchaseRequestedForCode"
                        value={item.purchaseRequestedForCode}
                        onChange={(e) => handleItemChange(index, e)}
                        disabled={
                          !item.purchaseRequestedFor ||
                          !(
                            requestedForCodeOptionsMap[index] &&
                            requestedForCodeOptionsMap[index].length > 0
                          )
                        }
                      >
                        <option value="">Select Code</option>
                        {(requestedForCodeOptionsMap[index] || []).map(
                          (opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          )
                        )}
                      </Form.Control>
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Office of Supply</Form.Label>
                      <Form.Control
                        as="select"
                        name="officeOfSupply"
                        value={item.officeOfSupply}
                        onChange={(e) => handleItemChange(index, e)}
                      >
                        <option value="">Select Office</option>
                        {offices.map((opt) => {
                          const val =
                            opt.officeCode ??
                            opt.code ??
                            opt.value ??
                            opt.officeId ??
                            opt.id;
                          const label =
                            opt.name ??
                            opt.officeName ??
                            opt.label ??
                            val;
                          return (
                            <option key={val} value={val}>
                              {val}-{label}
                            </option>
                          );
                        })}
                      </Form.Control>
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Store of Supply</Form.Label>
                      <Form.Control
                        as="select"
                        name="storeOfSupply"
                        value={item.storeOfSupply}
                        onChange={(e) => handleItemChange(index, e)}
                        disabled={stores.length === 0}
                      >
                        <option value="">Select Store</option>
                        {stores.map((opt) => {
                          const val =
                            opt.storeCode ??
                            opt.code ??
                            opt.value ??
                            opt.id;
                          const label =
                            opt.name ??
                            opt.storeName ??
                            opt.label ??
                            val;
                          return (
                            <option key={val} value={val}>
                              {val} - {label}
                            </option>
                          );
                        })}
                      </Form.Control>
                    </Form.Group>
                  </Col>

                  <Col md={3} className="d-flex align-items-end">
                    {orderItems.length > 1 && (
                      <Button
                        variant="danger"
                        onClick={() => removeItemRow(index)}
                      >
                        Remove
                      </Button>
                    )}
                  </Col>
                </Row>
              </div>
            ))}

            <Button variant="secondary" onClick={addItemRow}>
              + Add Another Item
            </Button>

            <hr />

            <div className="fields-btn-container mt-3">
              <Button
                variant="primary"
                type="submit"
                className="fields-submit"
                disabled={loading || dropdownLoading}
              >
                Save Purchase Order
              </Button>
            </div>
          </Form>
        </Container>
      </div>

      {(loading || dropdownLoading) && (
        <div className="loading-overlay">
          <Sample14 />
        </div>
      )}
    </>
  );
};

export default VendorInvoiceWithoutPO;
